import React, { useEffect, useMemo, useState } from 'react';
import './Catalog.css';
import { Link } from 'react-router-dom';
import { listCatalogItems, listCategories } from '../../api/catalog';

export default function Catalogo() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(() => sessionStorage.getItem('cat_q') || '');
  const [sort, setSort] = useState(() => sessionStorage.getItem('cat_sort') || 'recent');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catOrder, setCatOrder] = useState([]); 

  useEffect(() => {
    document.title = 'AITA - Catálogo Seminuevos';
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setError('');
        setLoading(true);

        const [data, cats] = await Promise.all([
          listCatalogItems().catch(() => []),
          listCategories().catch(() => [])
        ]);

        if (!alive) return;

        setItems(Array.isArray(data) ? data : (data?.items ?? []));
        setCatOrder(Array.isArray(cats) ? cats.filter(Boolean) : []);
      } catch (e) {
        console.error('Error cargando catálogo:', e);
        if (alive) setError(e?.message || 'No se pudo cargar el catálogo');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => { sessionStorage.setItem('cat_q', q); }, [q]);
  useEffect(() => { sessionStorage.setItem('cat_sort', sort); }, [sort]);

  useEffect(() => {
    if (loading) return;
    const y = sessionStorage.getItem('cat_scrollY');
    if (!y) return;
    sessionStorage.removeItem('cat_scrollY');
    requestAnimationFrame(() => window.scrollTo(0, parseInt(y, 10)));
  }, [loading]);

  const filtered = useMemo(() => {
    const txt = q.trim().toLowerCase();
    return (items || []).filter(i =>
      [i.title, i.description ?? ''].join(' ').toLowerCase().includes(txt)
    );
  }, [items, q]);

    const grouped = useMemo(() => {
    const byCat = new Map();
    const getCat = (it) => (it.category?.trim() ? it.category : 'Sin categoría');

    for (const it of filtered) {
      const key = getCat(it);
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key).push(it);
    }

    const sortItems = (arr) => {
      switch (sort) {
        case 'price-asc':
          return arr.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
        case 'price-desc':
          return arr.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        case 'recent':
        default:
          return arr.sort(
            (a, b) =>
              new Date(b.created_at || b.createdAt || 0) -
              new Date(a.created_at || a.createdAt || 0)
          );
      }
    };

    for (const [, arr] of byCat) sortItems(arr);
    const keys = Array.from(byCat.keys());
    const sinCat = 'Sin categoría';
    const named = keys.filter(k => k !== sinCat);

    const orderedNamed = catOrder.length
      ? named.sort((a, b) => (catOrder.indexOf(a) - catOrder.indexOf(b)))
      : named.sort((a, b) => a.localeCompare(b, 'es'));

    const resultKeys = [...orderedNamed, ...(byCat.has(sinCat) ? [sinCat] : [])];

    return resultKeys.map(k => ({ category: k, items: byCat.get(k) }));
  }, [filtered, sort, catOrder]);

  return (
    <div className="catalog-page">
      <div className="cat-wrap">
        <div className="cat-toolbar">
          <h2 className="cat-title">Catálogo Ofertas</h2>

          <div className="cat-search">
            <input
              placeholder="Buscar por título o descripción…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>

          <select
            className="cat-select"
            value={sort}
            onChange={e => setSort(e.target.value)}
          >
            <option value="recent">Más recientes</option>
            <option value="price-asc">Precio: menor a mayor</option>
            <option value="price-desc">Precio: mayor a menor</option>
          </select>
        </div>

        {loading ? (
          <div className="cat-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <article key={i} className="card skeleton">
                <div className="card-hero skel" />
                <div className="card-body">
                  <div className="skel big" />
                  <div className="skel mid" />
                  <div className="skel small" />
                </div>
              </article>
            ))}
          </div>
        ) : error ? (
          <div className="cat-empty">No se pudo cargar: {error}</div>
        ) : grouped.length === 0 ? (
          <div className="cat-empty">
            No hay artículos que coincidan con la búsqueda.
          </div>
        ) : (
          grouped.map(section => (
            <section key={section.category} className="cat-section">
              <h3 className="cat-section-title">{section.category}</h3>
              <div className="cat-grid">
                {section.items.map(item => (
                  <CatalogCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function CatalogCard({ item }) {
  const [hero, setHero] = useState(item.images?.[0] || null);
  const thumbs = (item.images || []).slice(0, 6);

  useEffect(() => {
    setHero(item.images?.[0] || null);
  }, [item.images]);

  const saveScroll = () => sessionStorage.setItem('cat_scrollY', window.scrollY);

  return (
    <article className="card">
      <Link
        to={`/catalogo/${item.id}`}
        onClick={saveScroll}
        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
      >
        <div className="card-hero">
          {hero ? (
            <img src={hero} alt={item.title} loading="lazy" />
          ) : (
            <span style={{ color: '#80889a', fontSize: 13 }}>Sin imagen</span>
          )}
          <div className="price-chip">{formatMoney(item.price)} MXN</div>
        </div>
      </Link>

      <div className="card-body">
        <h3 className="card-title">{item.title}</h3>
        <p className="card-desc clamp-3">{item.description || '—'}</p>

        {thumbs.length > 1 && (
          <div className="thumb-row">
            {thumbs.map((img, i) => (
              <button
                key={i}
                className="thumb"
                title="Ver"
                onClick={() => setHero(img)}
                aria-label="Cambiar imagen"
              >
                <img src={img} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        <div className="card-actions">
          <Link to={`/catalogo/${item.id}`} onClick={saveScroll} className="btn btn-primary">
            Cotizar
          </Link>
        </div>
      </div>
    </article>
  );
}

function formatMoney(n) {
  const v = Number(n ?? 0);
  try {
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(v);
  } catch {
    return v.toFixed(2);
  }
}
